import React, {ReactElement, useContext, useEffect, useState} from 'react';

import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {useSettings} from "../../../contexts/settings/SettingsContext";

import {useHistory, useParams} from "react-router-dom";
import AcctRenovationsTable from "./components/AcctRenovationsTable";
import {BudgetingType} from "../../../BudgetingType";

import {RenderIfReadOnly} from "../../../authorization/RenderIfAuthorized";
import ReadOnlyHeader from "../../../components/navbar/ReadOnlyHeader";

import * as navStyles from "../../../components/navbar/styles.module.scss";

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowCSS from "../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../components/workflows/workflow-header/styles/css.module.scss";

import WorkflowHeader from "../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowPageLabel from "../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET,
    WORKFLOW_YEAR_TAB_REFORECAST
} from "../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../logic/workflows";
import WorkflowNavToggle from "../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import {UpdateRenovationPackageCountMutationOptions, useAcctRenovationPackagesLazyQuery, useGetAcctRenoMoveOutRatesLazyQuery, useGetForecastLocksLazyQuery, useUpdateRenovationPackageCountMutation, VersionType} from '../../../__generated__/generated_types';
import {userIsAdmin, userIsReadOnly} from '../../../authorization/AuthorizationCheckers';
import {AuthContext} from '../../../contexts/AuthContext';
import {useRenovationsWorkflow} from '../../../hooks/useRenovationsWorkflow';
import {toast} from 'react-toastify';
import AnalystRenovationDrawer from '../../../components/analyst-renovations-drawers/AnalystRenovationDrawer';
import useGetClientCostCategories from '../../../components/analyst-renovations-drawers/data/UseGetClientCostCategories';


function AccountRenovations(props: IWorkflowPageProps & {budgetYear: number}): ReactElement {
    const {user} = useContext(AuthContext);
    const history = useHistory();
    const {currentProperty: property} = useProperties();

    const {unitTypeId, type} = useParams() as {unitTypeId: string; type: string};
    const versionType = type == "budget" ? VersionType.Budget : VersionType.Reforecast;
    const {renovationsWorkflow, renovationsWorkflowDataLoading} = useRenovationsWorkflow({categoryType: type, budgetYear: props.budgetYear});

    const {year} = useSettings();
    const currentVersionYear = type == BudgetingType.BUDGET ? year + 1 : year;

    const [getForecastLocks, {data: dataForecastLocks, loading: lockDataLoading}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache",
    });
    const {
        data: renovationCostCategories,
    } = useGetClientCostCategories(props.budgetYear);

    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const [thisUnitType, setThisUnitType] = useState<{id: string, type: string}>();
    const [readOnly, setReadOnly] = useState(true);
    const [showDrawer, setShowDrawer] = useState(false);

    const [
        getAcctRenovationPackages,
        {data: renovationData, loading: renovationDataLoading}
    ] = useAcctRenovationPackagesLazyQuery({
        fetchPolicy: "network-only",
    });
    const [
        getMoveOutRates,
        {data: moveOutRatesData, loading: moveOutRatesLoading}
    ] = useGetAcctRenoMoveOutRatesLazyQuery();
    const [updateRenovationPackageCountMutation] = useUpdateRenovationPackageCountMutation();

    async function saveChanges(payloads: UpdateRenovationPackageCountMutationOptions[]) {
        try {
            const results = await Promise.allSettled(payloads.map(payload => updateRenovationPackageCountMutation(payload)));

            if (results.every(result => result.status === "fulfilled")) {
                toast.success("Counts updated");
            }
        } catch {
            toast.error("There was a problem saving, please refresh and try again");
        }
    }

    useEffect(() => {
        if (!property?.id) {
            return;
        }

        setThisUnitType(undefined);
        getForecastLocks({
            variables: {
                propertyId: property.id,
                budgetYear: props.budgetYear,
            }
        });
    }, [property, props.budgetYear]);

    useEffect(() => {
        const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
        if (updateWorkflowTab != false) {
            setSelectedTab(updateWorkflowTab as string);
        }
    }, [type]);

    useEffect(() => {
        if(!versionType || !property || !currentVersionYear || !year || !unitTypeId){
            return;
        }

        getAcctRenovationPackages({
            variables: {
                propertyIds: [property.id],
                yearAndType: {
                    versionType,
                    year: currentVersionYear,
                },
                budgetYear: props.budgetYear
            },
        });

        getMoveOutRates({
            variables: {
                propertyId: property.id,
                year: currentVersionYear,
                budgetYear: props.budgetYear,
                unitTypeId,
                getReforecast: versionType == VersionType.Reforecast,
            }
        });
    }, [versionType, property, currentVersionYear, year, unitTypeId]);

    useEffect(() => {
        if (!unitTypeId || !renovationsWorkflow || renovationsWorkflowDataLoading) {
            return;
        }

        // using renovationsWorkflow here because that's what the sidebar nav exposes as available urls for viewing renovations for unit types
        const unitTypesAvailableForNav = renovationsWorkflow[0]?.children;

        if (!unitTypesAvailableForNav) {
            return;
        }

        // TreeNodes for unit types use the subtitle field for unitTypeId
        const thisUnitTypeLocal = unitTypesAvailableForNav.find(unitType => unitType.subtitle == unitTypeId);

        // if the user changes their property while viewing a unit type, the url param 'unitTypeId' becomes invalid
        // so we check to make sure the 'unitTypeId' requested in the url actually exists on the property, otherwise redirect them
        // to the first available unit type for the newly selected property, or to the start page if no valid unit types with renovations exist
        if (thisUnitTypeLocal && thisUnitTypeLocal.subtitle) {
            setThisUnitType({
                id: thisUnitTypeLocal.subtitle,
                type: thisUnitTypeLocal.title,
            });
        } else {
            // get the first accessible unit type for the newly selected property
            const firstAvailableUnitTypeId = unitTypesAvailableForNav[0]?.subtitle;

            if (firstAvailableUnitTypeId) {
                history.push(`/renovations/${firstAvailableUnitTypeId}/${type}`);
            } else {
                toast.error(`No unit types with renovation packages found for ${property ? property.name : 'this property'}`);
                history.push("/setup/start");
            }
        }
    }, [unitTypeId, renovationsWorkflow, renovationsWorkflowDataLoading]);

    useEffect(() => {
        if (!dataForecastLocks?.forecastLocks || lockDataLoading) {
            return;
        }

        const versionLocked = selectedTab === WORKFLOW_YEAR_TAB_REFORECAST ? dataForecastLocks.forecastLocks.property.reforecastLocked : dataForecastLocks.forecastLocks.property.budgetLocked;

        if (!versionLocked && !userIsReadOnly(user)) {
            setReadOnly(false);
        }
    }, [lockDataLoading, dataForecastLocks]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <RenderIfReadOnly>
                <ReadOnlyHeader className={navStyles.readOnlyHeaderFixed} />
            </RenderIfReadOnly>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle />
                            {thisUnitType &&
                                <WorkflowPageLabel label={thisUnitType.type} />
                            }
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} />
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>

                <div className={workflowCSS.bodyContainer}>
                    <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.accountYearTabNav}>
                        {property &&
                            <WorkflowYearTabs
                                property={property}
                                selectedTab={selectedTab}
                                rawForecastLocks={dataForecastLocks}
                            />
                        }
                        <div className={workflowCSS.tableWrapper}>
                            <AcctRenovationsTable
                                renovationData={renovationData}
                                moveOutRatesData={moveOutRatesData}
                                dataLoading={renovationDataLoading || moveOutRatesLoading || renovationsWorkflowDataLoading}
                                currentVersionYear={currentVersionYear}
                                property={property}
                                versionType={versionType}
                                unitTypeId={unitTypeId}
                                readOnly={readOnly}
                                userIsAdmin={userIsAdmin(user)}
                                budgetYear={props.budgetYear}
                                onOpenDrawer={() => setShowDrawer(true)}
                                onSaveChanges={saveChanges}
                            />
                        </div>
                    </Tabs>
                </div>
            </div>

            {userIsAdmin(user) && property && thisUnitType &&
                <AnalystRenovationDrawer
                    show={showDrawer}
                    propertyId={property.id}
                    unitTypeId={thisUnitType.id}
                    unitTypeName={thisUnitType.type}
                    renovationCostCategories={renovationCostCategories}
                    handleClose={(triggerRefetch) => {
                        if (triggerRefetch) {
                            getAcctRenovationPackages({
                                variables: {
                                    propertyIds: [property.id],
                                    yearAndType: {
                                        versionType,
                                        year: currentVersionYear,
                                    },
                                    budgetYear: props.budgetYear
                                },
                            });
                        }
                        setShowDrawer(false);
                    }}
                    budgetYear={props.budgetYear}
                />
            }
        </ThemeProvider>
    );
}

export default React.memo(AccountRenovations);
